'use client';

import Link from 'next/link';
import Image from '@/components/media/SiteImage';
import { useTranslations } from 'next-intl';
import { trackEvent } from '@/lib/analytics';

type FLink = { href: string; literal?: string; nav?: string; foot?: string };

const cabinLinks: FLink[] = [
  { href: '/levi', nav: 'cabinsLevi' },
  { href: '/yllas', nav: 'cabinsYllas' },
  { href: '/cabins/skycabin-levi', literal: 'Skycabin' },
  { href: '/cabins/levikka-yllas', literal: 'Levikka' },
  { href: '/cabins/varakka-yllas', literal: 'Varakka' },
  { href: '/cabins/iso-yllas', literal: 'Iso-Ylläs' },
  { href: '/cabins/pikupoikka', literal: 'PikuPoikka' },
  { href: '/cabins/isopoikka', literal: 'IsoPoikka' },
  { href: '/cabins/mestari', literal: 'Mestari' },
  { href: '/cabins', nav: 'allCabins' },
  { href: '/cabins/compare', nav: 'compareCabins' },
  { href: '/reviews', foot: 'reviews' },
];

const toolLinks: FLink[] = [
  { href: '/weather', nav: 'weather' },
  { href: '/aurora', nav: 'aurora' },
  { href: '/road-conditions', nav: 'roadConditions' },
  { href: '/getting-here', nav: 'gettingHere' },
];

const exploreLinks: FLink[] = [
  { href: '/things-to-do', foot: 'thingsToDo' },
  { href: '/activities', foot: 'activities' },
  { href: '/dining', foot: 'dining' },
  { href: '/culture', foot: 'culture' },
  { href: '/guest-guide', foot: 'guestGuide' },
  { href: '/sustainability', foot: 'sustainability' },
];

const moreLinks: FLink[] = [
  { href: '/blog', foot: 'blog' },
  { href: '/about', foot: 'about' },
  { href: '/gallery', foot: 'gallery' },
  { href: '/faq', foot: 'faq' },
  { href: '/seasonal-staff', foot: 'staffAccommodation' },
  { href: '/contact', foot: 'contact' },
];

const linkClasses = 'inline-flex min-h-11 items-center break-words text-sm transition-colors duration-200 hover:text-[#C4A95E] text-[#5A5A6E]';
const headingClasses =
  'font-[family-name:var(--font-outfit)] text-xs font-light uppercase tracking-[0.15em] mb-3 text-[#1A1A2E]';

// Contact details and brand copy are editable in the admin ("Site-wide / Contact & Footer");
// the public layout resolves them from SiteSettings and passes them in as props.
export interface FooterProps {
  email: string;
  phone: string;
  instagram: string;
  tagline: string;
  blurb: string;
}

export default function Footer({ email, phone, instagram, tagline, blurb }: FooterProps) {
  const t = useTranslations('Footer');
  const tn = useTranslations('Nav');
  const label = (l: FLink) => l.literal ?? (l.nav ? tn(l.nav) : t(l.foot as string));
  const phoneHref = `tel:${phone.replace(/\s/g, '')}`;
  const instagramUrl = `https://www.instagram.com/${instagram.replace(/^@/, '')}`;

  const columns: { heading: string; links: FLink[] }[] = [
    { heading: t('colCabins'), links: cabinLinks },
    { heading: t('colTools'), links: toolLinks },
    { heading: t('colExplore'), links: exploreLinks },
    { heading: t('colMore'), links: moreLinks },
  ];

  return (
    <footer className="bg-[#F0EDE5]">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid grid-cols-2 gap-x-6 gap-y-8 md:grid-cols-3 xl:grid-cols-6">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-3 xl:col-span-1">
            <div className="mb-3">
              <Image
                src="/images/logo.png"
                alt="Kettura Finland"
                width={64}
                height={64}
                className="h-16 w-16 object-contain"
              />
            </div>
            <p className="text-sm font-medium mb-4 text-[#9B7B3A]">{tagline}</p>
            <p className="text-sm leading-relaxed text-[#5A5A6E]">{blurb}</p>
          </div>

          {/* Link columns */}
          {columns.map((col) => (
            <div key={col.heading}>
              <p className={headingClasses}>{col.heading}</p>
              <ul className="flex flex-col gap-0">
                {col.links.map((link) => (
                  <li key={link.href}>
                    <Link href={link.href} className={linkClasses}>
                      {label(link)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact Column */}
          <div className="col-span-2 md:col-span-1 min-w-0">
            <p className={headingClasses}>{t('colContact')}</p>
            <ul className="flex flex-col gap-0">
              <li>
                <a href={`mailto:${email}`} className={linkClasses}>
                  {email}
                </a>
              </li>
              <li>
                <a
                  href={phoneHref}
                  onClick={() => trackEvent('phone_click')}
                  className={linkClasses}
                >
                  {phone}
                </a>
              </li>
              <li>
                <a
                  href={instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('outbound_link_click', { url: instagramUrl })}
                  className={linkClasses}
                >
                  {instagram}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#9B7B3A]/30">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-1">
            <p className="inline-flex min-h-11 items-center text-xs text-[#5A5A6E]">
              &copy; 2026 Kettura Finland. {t('rights')}
            </p>
            <p className="inline-flex min-h-11 items-center text-xs text-[#5A5A6E]/70">
              Kettura Finland Oy, 3611789-2, Ylläksentie 1428, 99100 Kittilä
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <Link
              href="/privacy"
              className="inline-flex min-h-11 items-center text-xs text-[#5A5A6E] hover:text-[#9B7B3A] transition-colors duration-200"
            >
              {t('privacy')}
            </Link>
            <Link
              href="/terms"
              className="inline-flex min-h-11 items-center text-xs text-[#5A5A6E] hover:text-[#9B7B3A] transition-colors duration-200"
            >
              {t('terms')}
            </Link>
            <Link
              href="/accessibility"
              className="inline-flex min-h-11 items-center text-xs text-[#5A5A6E] hover:text-[#9B7B3A] transition-colors duration-200"
            >
              Accessibility
            </Link>
            <Link
              href="/admin"
              className="inline-flex min-h-11 items-center text-xs text-[#5A5A6E]/40 hover:text-[#9B7B3A] transition-colors duration-200"
            >
              {t('admin')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
